import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { tagsApi } from "api/instances";

import { PostFeedFilters } from "../../../../models";

import AuthoringSearch from "modules/common/components/filters/authoringSearch";
import FilterContainer from "modules/common/components/filters/filterContainer";
import FilterDropdown from "modules/common/components/filters/filterDropdown";

import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { SelectOption } from "modules/common/models";
import { getAvailableTags } from "utils/filterTagsUtils";


const availablePostTypes: SelectOption[] = [
  { key: "standard", text: "Standard", type: "item" },
  { key: "mandatory", text: "Mandatory", type: "item" },
  { key: "compliance", text: "Compliance", type: "item" },
  { key: "public", text: "Public", type: "item" }
];

class Filters extends React.Component<PropsWithRedux, ComponentState> {
  constructor(props: PropsWithRedux) {
    super(props);

    this.state = {
      canSearch: !!props.filters.postTypes!.length || !!props.filters.tags!.length || !!props.filters.textToSearch,
      lcidToSearch: props.filters.lcidToSearch || "",
      textToSearch: props.filters.textToSearch || "",
      tagPostCounts: undefined, // populate after tags are retrieved / component is mounted
    };
  }

  public componentDidUpdate(prevProps: PropsWithRedux) {
    if (this.props.filters.lcidToSearch !== prevProps.filters.lcidToSearch)
      this.setState({ lcidToSearch: this.props.filters.lcidToSearch || "" });
    
    if (this.props.filters.textToSearch !== prevProps.filters.textToSearch)
      this.setState({ textToSearch: this.props.filters.textToSearch || "" });
  }

  public async componentDidMount() {
    this.setState({ tagPostCounts: await tagsApi.getTagPostCounts() });
  }

  public render() {
    const { filters } = this.props;

    const availableTags: SelectOption[] = getAvailableTags(this.props.tagSettings, this.state.tagPostCounts);

    return (
        <FilterContainer
          filters={
            <React.Fragment>
              <TextField
                variant="outlined"
                size="small"
                value={this.state.textToSearch}
                placeholder="Search posts"
                InputProps={{
                  startAdornment: (
                    <SearchIcon className="search-icon" />
                  ),
                }}
                onChange={this.onUpdateTextToSearch}
                onKeyUp={this.onKeyUp}
                className="text-to-search"
              />
              <FilterDropdown text="Topics">
                <List disablePadding>
                  {availableTags.map((availableTag) => {
                    if (availableTag.type === "header")
                      return <ListSubheader key={availableTag.key} disableSticky className="select-popover-group-header">{availableTag.text}</ListSubheader>;
                    return (
                      <ListItem key={availableTag.key} dense button onClick={() => this.onChangeTags(availableTag.key)}>
                        <ListItemIcon className="callout-checkbox">
                          <Checkbox
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            color="primary"
                            checked={!!filters.tags!.find((selectedTag) => selectedTag === availableTag.key)}
                          />
                        </ListItemIcon>
                        <ListItemText primary={availableTag.text} />
                      </ListItem>
                    );
                  })}
                </List>
                  </FilterDropdown>
              <FilterDropdown text="Types">
                <List disablePadding>
                  {availablePostTypes.map((availablePostType) => (
                    <ListItem key={availablePostType.key} dense button onClick={() => this.onChangePostTypes(availablePostType.key)}>
                      <ListItemIcon className="callout-checkbox">
                        <Checkbox
                          edge="start"
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          color="primary"
                          checked={!!filters.postTypes!.find((selectedPostType) => selectedPostType === availablePostType.key)}
                        />
                      </ListItemIcon>
                      <ListItemText primary={availablePostType.text} />
                    </ListItem>
                  ))}
                </List>
              </FilterDropdown>
            </React.Fragment>
          }
          filterCommands={
            <AuthoringSearch
              canSearch={this.state.canSearch}
              onApplyFilters={this.onChangeTextToSearch}
              onClearFilters={this.onClearFilters}
            />
          }
          filterSelection={this.getFilterSelection()}
        />
    );
  }


  private getFilterSelection = (): JSX.Element | undefined => {
    const { filters } = this.props;

    if (!filters.textToSearch && !filters.tags!.length && !filters.postTypes!.length)
      return undefined;

    const availableTags: SelectOption[] = getAvailableTags(this.props.tagSettings, this.state.tagPostCounts);

    return (
      <React.Fragment>
        {!!filters.textToSearch &&
          <Chip
            key="search-text"
            label={`"${filters.textToSearch}"`}
            onDelete={this.onClearTextToSearch}
            deleteIcon={<CloseIcon />}
          />
        }
        {filters.tags!.map((selectedTag) =>
          <Chip
            key={selectedTag}
            label={availableTags.find((tag) => tag.key === selectedTag)!.text}
            onDelete={() => this.onClearTag(selectedTag)}
            deleteIcon={<CloseIcon />}
          />
        )}
        {filters.postTypes!.map((selectedPostType) =>
          <Chip
            key={selectedPostType}
            label={availablePostTypes.find((postType) => postType.key === selectedPostType)!.text}
            onDelete={() => this.onClearPostType(selectedPostType)}
            deleteIcon={<CloseIcon />}
          />
        )}
      </React.Fragment>
    );
  }


  private onChangeFilters = (value: Partial<PostFeedFilters>) => {
    this.props.onChangeFilters({ ...this.props.filters, lcidToSearch: this.state.lcidToSearch, textToSearch: this.state.textToSearch, ...value, ignoreSubscriptions: true });
    
    let currentFilters = {...this.props.filters, ...value};
    if ( currentFilters.postTypes?.length === 0 && currentFilters.postStates?.length === 0 && currentFilters.tags?.length === 0 && this.state.textToSearch === "") {
      this.clearCanSearch();
    }
    else {
      this.setCanSearch();
    }
  }

  private onChangeLcidToSearch = (lcidToSearch: string) => {
    this.onChangeFilters({ lcidToSearch });
  }

  private onChangePostTypes = (currentPostType: string) => {
    const hasSelectedPostType: boolean = !!this.props.filters.postTypes!.find((selectedPostType) => selectedPostType === currentPostType);
    if (hasSelectedPostType)
      this.onChangeFilters({ postTypes: this.props.filters.postTypes!.filter((selectedPostType) => selectedPostType !== currentPostType) });
    else
      this.onChangeFilters({ postTypes: this.props.filters.postTypes!.concat([currentPostType]) });
  }

  private onChangeTags = (currentTag: string) => {
    const hasSelectedTag: boolean = !!this.props.filters.tags!.find((selectedTag) => selectedTag === currentTag);
    if (hasSelectedTag)
      this.onChangeFilters({ tags: this.props.filters.tags!.filter((selectedTag) => selectedTag !== currentTag) });
    else
      this.onChangeFilters({ tags: this.props.filters.tags!.concat([currentTag]) });
  }

  private onChangeTextToSearch = () => {
    this.onChangeFilters({ textToSearch: this.state.textToSearch });
  }

  private onClearFilters = () => {
    this.clearCanSearch();
    this.props.onClearFilters();
  }

  private onClearPostType = (postType: string) => {
    this.onChangeFilters({ postTypes: this.props.filters.postTypes!.filter((selectedPostType) => selectedPostType !== postType) });
  }

  private onClearTag = (tag: string) => {
    this.onChangeFilters({ tags: this.props.filters.tags!.filter((selectedTag) => selectedTag !== tag) });
  }

  private onClearTextToSearch = () => {
    this.onChangeFilters({ textToSearch: "" });

    if(this.hasNoFilters()) {
      this.setState({canSearch: false});
    }
  }

  private onKeyUp = (key) => {
    if (key.keyCode === 13)
      this.onChangeTextToSearch();
  }

  private onUpdateLcidToSearch = (event) => {
    this.setState({ lcidToSearch: event.target.value });
    if (!!this.props.filters.textToSearch)
      this.onChangeLcidToSearch(event.target.value);
  }

  private onUpdateTextToSearch = (event) => {
    this.setState({ textToSearch: event.target.value });

    if(event.target.value !== "" && !this.state.canSearch) {
      this.setState({canSearch: true});
    }
    else if(event.target.value === "" && this.hasNoFilters()) {
      this.setState({canSearch: false});
    }
  }

  private hasNoFilters = () => {
    return (
      this.props.filters.postTypes?.length === 0 
      && this.props.filters.postStates?.length === 0 
      && this.props.filters.tags?.length === 0);
  }

  private clearCanSearch = () => {
    this.setState({ canSearch: false });
  }

  private setCanSearch = () => {
    this.setState({ canSearch: true });
  }
}

interface ComponentProps {
  onChangeFilters: (filters: Partial<PostFeedFilters>) => void;
  onClearFilters: () => void;
}

interface ComponentState {
  canSearch: boolean;
  lcidToSearch: string;
  textToSearch: string;
  tagPostCounts: { [key: string]: number; } | undefined;
}

const connector = connect(
  (state: GlobalApplicationState, ownProps: ComponentProps) => ({
    ...ownProps,
    filters: state.posts.postsFeed.filters,
    clientSettings: state.settings.clientSettings,
    lcidMappings: state.resources.lcidMappings,
    tagSettings: state.settings.tagSettings
  })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(Filters);